import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../../lib/utils';

/**
 * KpiPill — Discovr AI design system (Figma "KPI Pills" component).
 *
 * Small status pill (e.g. "+30% above goal") built on the additive Figma
 * token layer (see design-system/tokens.css). The `variant` values mirror
 * Figma's "Property 1" (Green/Yellow/Grey/Red); each maps to a status token
 * pair:
 *   green  → success (above goal / positive)
 *   yellow → warning (watch)
 *   red    → destructive (below goal / negative)
 *   grey   → neutral
 *
 * Width is content-driven (`w-fit`); the Figma instance's fixed 93px was just
 * the measured width of its sample label. Text color uses `text-[color:var(…)]`
 * so it doesn't collide with the `text-[10px]` size utility in tailwind-merge.
 */
const kpiPillVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-full px-1.5 py-0.5 font-dm-sans text-[10px] leading-[normal] font-medium',
  {
    variants: {
      variant: {
        green:
          'bg-background-success-light-hover text-(--foreground-success-default)',
        yellow:
          'bg-background-warning-light-hover text-(--foreground-warning-default)',
        red: 'bg-background-destructive-light-hover text-(--foreground-destructive-default)',
        grey: 'bg-(--beige-200) text-(--foreground-secondary-default)',
      },
    },
    defaultVariants: {
      variant: 'green',
    },
  },
);

type KpiPillProps = React.ComponentProps<'span'> &
  VariantProps<typeof kpiPillVariants> & {
    asChild?: boolean;
  };

function KpiPill({ className, variant, asChild = false, ...props }: KpiPillProps) {
  const Comp = (asChild ? Slot.Root : 'span') as React.ElementType;

  return (
    <Comp
      data-slot="kpi-pill"
      data-variant={variant}
      className={cn(kpiPillVariants({ variant }), className)}
      {...props}
    />
  );
}

export { KpiPill, kpiPillVariants };
export type { KpiPillProps };
